/**
 * What a community's pull and push compare (gh #5003, Sync rules): each synced entity's
 * normalized content, its `rev`, the `client_id` of its create, and the three-way verdict.
 *
 * Pure: no files, no requests. `sync.ts` reads both sides and acts on the verdicts.
 *
 * A REV is `v1:` + a short hash of a pinned canonical serialization of an entity's normalized
 * content, keyed by its kind and id. It is computed the same way from a server answer and from
 * the files, so the base an entity was last synced from travels with the files: git merges it per
 * entity, and no machine-local state exists. A rev covers an entity's own content, never its
 * place. A later CLI that changes a field set bumps `REV_VERSION`; a rev of another version is
 * read as no base at all, so the entity re-bases (`classify`) instead of reading as changed.
 */

import { createHash } from 'node:crypto';
import { slugifyKebab, uuidV5 } from '@wayai/core';
import type { CommunityRoom, CommunitySettings } from '@wayai/core/contracts';

export const REV_VERSION = 'v1';

/** CRLF and a lone CR read as LF, on both sides. */
export function toLf(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

// ── Normalized content: the values compared, after the contracts' own transforms ──

/** Settings and `rules.md` share one rev (`settings.rev`). */
export interface SettingsContent {
  /** Trimmed, as the server stores it; '' reads as none (`communitySettingsBody`). */
  rules: string | null;
  slow_mode_seconds: number;
  courses_label: string;
}

export interface JoinLinkContent {
  enabled: boolean;
}

/** A room's kind is not content: it never changes once created (a read-only field). */
export interface RoomContent {
  name: string;
  archived: boolean;
}

export function normalizeRules(rules: string | null | undefined): string | null {
  if (rules === null || rules === undefined) return null;
  const trimmed = toLf(rules).trim();
  return trimmed === '' ? null : trimmed;
}

export function settingsContent(settings: { rules: string | null; slow_mode_seconds: number; courses_label?: string }): SettingsContent {
  return {
    rules: normalizeRules(settings.rules),
    slow_mode_seconds: settings.slow_mode_seconds,
    courses_label: (settings.courses_label ?? '').trim(),
  };
}

export function roomContent(room: { name: string; archived: boolean }): RoomContent {
  return { name: toLf(room.name).trim(), archived: room.archived };
}

// ── Revs ──

function rev(parts: Array<string | number | boolean | null>): string {
  return `${REV_VERSION}:${createHash('sha256').update(JSON.stringify(parts)).digest('hex').slice(0, 12)}`;
}

export const settingsRev = (communityId: string, c: SettingsContent): string =>
  rev(['settings', communityId, c.rules, c.slow_mode_seconds, c.courses_label]);

export const joinLinkRev = (communityId: string, c: JoinLinkContent): string =>
  rev(['join_link', communityId, c.enabled]);

export const roomRev = (roomId: string, c: RoomContent): string => rev(['room', roomId, c.name, c.archived]);

/** A server answer's settings, as content and rev. */
export function serverSettings(communityId: string, settings: CommunitySettings): { content: SettingsContent; rev: string } {
  const content = settingsContent(settings);
  return { content, rev: settingsRev(communityId, content) };
}

export function serverRoom(room: CommunityRoom): { content: RoomContent; rev: string } {
  const content = roomContent(room);
  return { content, rev: roomRev(room.room_id, content) };
}

// ── The three-way verdict (the Sync rules table) ──

/**
 * - `in_sync`   — files and server both still at the base: nothing to do.
 * - `local`     — the files changed: push writes them; pull keeps them (an unpushed edit).
 * - `remote`    — the server changed: pull takes it; push leaves it ("pull to take it").
 * - `converged` — both changed alike (or no base, but they agree): refresh the rev, write nothing.
 * - `conflict`  — both changed, differently (or no base, and they differ): push refuses the
 *                 entity; pull reports it, and `--theirs` takes the server's.
 */
export type SyncState = 'in_sync' | 'local' | 'remote' | 'converged' | 'conflict';

export function classify(base: string | undefined, file: string, server: string): SyncState {
  const known = base !== undefined && base.startsWith(`${REV_VERSION}:`) ? base : undefined;
  if (known === undefined) return file === server ? 'converged' : 'conflict';
  const fileChanged = file !== known;
  const serverChanged = server !== known;
  if (!fileChanged && !serverChanged) return 'in_sync';
  if (fileChanged && !serverChanged) return 'local';
  if (!fileChanged) return 'remote';
  return file === server ? 'converged' : 'conflict';
}

// ── Creates: the stable local name → `client_id` (Decision 9) ──

export type CreateKind = 'room' | 'course' | 'module' | 'lesson';

/**
 * The `client_id` of a create: a UUIDv5 (RFC 9562 §5.5) in the community's id as namespace, of
 * the entity's kind and its stable local name. Deterministic, so a create retried after a lost
 * response — or after the files lost an id the push wrote — answers the entity made the first
 * time instead of making a second one.
 */
export function createClientId(communityId: string, kind: CreateKind, localName: string): Promise<string> {
  return uuidV5(communityId, `${kind}:${localName}`);
}

/**
 * A stable local name for an entity first written by pull: its name's slug, or `fallback` when
 * the name has none; one already taken becomes `<slug>--<id-prefix>` (the Sync rules).
 */
export function localName(name: string, id: string, taken: ReadonlySet<string>, fallback: string): string {
  const slug = slugifyKebab(name) || fallback;
  if (!taken.has(slug)) return slug;
  const withPrefix = `${slug}--${id.slice(0, 8)}`;
  return taken.has(withPrefix) ? `${slug}--${id}` : withPrefix;
}
